import * as PIXI from "pixi.js";
import { ColorOverlayFilter, ColorReplaceFilter } from 'pixi-filters';
import EventApi from "../../../../../../api/EventsApi";
import MessageHelper from "../../../../../../helpers/MessageHelper";
import PixiMessenger from "../PixiMessenger";
import PixiPainter from "../../PixiPainter";
import PixiSelector from "../PixiSelector";
import {ICardInfo} from "../../PixiPainter";
import Scribe from "./Scribe";
//@ts-ignore
import store from "../../../../../../store";

enum Tool { Select, Brush, Text, Eraser, Trash }
const MARGIN = 32;
const ALPHA = 0;
const CAN_DRAW = 'canTableDrawOnCards';

export default class Painter extends PIXI.Container {

    private readonly renderer: PIXI.Renderer | PIXI.AbstractRenderer;
    private easelWidth:  number;
    private easelHeight: number;
    private readonly easel = new PIXI.Container();
    private readonly uiCont = new PIXI.Container();
    private readonly margin = MARGIN;
    private readonly easelFace = new PIXI.Graphics();
    private readonly eraserMask = new PIXI.Graphics();
    private readonly textFrame = new PIXI.Graphics();
    private readonly textCont = new PIXI.Container();
    private readonly brush = new PIXI.Graphics();
    private readonly textStyle: Partial<PIXI.ITextStyle>;
    private readonly   colors = [ 0xf44336, 0xff9800, 0xffeb3b, 0x4caf50, 0x00bcd4, 0x3f51b5, 0x9c27b0 ];
    private readonly bgColors = [ 0xffcdd2, 0xffe0b2, 0xfff9c4, 0xc8e6c9, 0xb2ebf2, 0xc5cae9, 0xe1bee7 ];
    private readonly colorButtons: Array<PIXI.Container> = [];

    private brushDown = false;
    private brushVisible = false;
    private brushSize = 32;
    private eraserSize = 64;
    private brushOld: { x: number, y: number } | null = null;
    private brushPos: { x: number, y: number } | null = null;
    private colorIdx = 0;
    private bgColorIdx = -1;
    private bgColor = 0xffffff;
    private playerColor: number = 0;
    private rightClick = false;
    private rightClickBtn = false;

    private elapsedTime = 0;
    private toolIdx = Tool.Brush;
    private pixiText = new PIXI.Text('');
    private text = '';
    private textEdit = false;
    private textFrameWhite = false;
    private mainTexture: PIXI.RenderTexture;
    private mainSprite: PIXI.Sprite;
    private imgSprite: PIXI.Sprite;
    private bgSprite: PIXI.Sprite;
    private imgSize = { width: 0, height: 0 };
    private mat = new PIXI.Sprite(PIXI.Texture.WHITE);
    private eraser: PIXI.Sprite | null = null;
    private mainMask: PIXI.Sprite | null = null;
    private test: PIXI.Container = new PIXI.Container;

    private imgScale = 1;
    private editMode = false;
    private card: ICardInfo | null = null;
    private points:Array<{ x: number, y: number }> = [];
    private actions:Array<any> = [];

    constructor(renderer: PIXI.Renderer | PIXI.AbstractRenderer, img: HTMLImageElement) {

        super();
        const imgScale = Math.min((img.width - Painter.margins.x) / img.naturalWidth, (img.height - Painter.margins.y) / img.naturalHeight);
        const width = img.naturalWidth * imgScale, height = img.naturalHeight * imgScale;
        this.name = 'Painter';
        this.renderer = renderer;
        this.easelWidth = width;
        this.easelHeight = height;
        this.imgScale = imgScale;

        this.mat.width  = this.easelWidth  + Painter.margins.x;
        this.mat.height = this.easelHeight + Painter.margins.y;
        this.mat.alpha = ALPHA;
        this.mat.tint = 0;
        this.mat.interactive = true;
        this.addChild(this.mat);

        this.mainTexture = PIXI.RenderTexture.create({ width: this.easelWidth, height: this.easelHeight });
        this.mainSprite = new PIXI.Sprite(this.mainTexture);

        this.imgSize = { width, height };
        this.imgSprite = PIXI.Sprite.from(PIXI.Texture.WHITE);
        this.bgSprite = PIXI.Sprite.from(PIXI.Texture.WHITE);

        this.pixiText.style = new PIXI.TextStyle({
            align: 'center',
            fill: this.colors[0],
            fontFamily: 'Arial',
            fontSize: 54,
            fontWeight: 'bold',
            stroke: 0xffffff,
            strokeThickness: 1,
            lineJoin: 'round'
        });
        this.textStyle = this.pixiText.style;

        this.addEasel();
        this.easel.addChild(this.mainSprite);
        this.easel.addChild(this.easelFace);
        this.addText();

        this.eraser = new PIXI.Sprite(PIXI.Texture.WHITE);
        this.easel.addChild(this.eraser);
        this.eraser.addChild(this.eraserMask);
        this.eraser.mask = this.eraserMask;

        this.mainMask = new PIXI.Sprite(PIXI.Texture.WHITE);
        this.easel.addChild(this.mainMask);
        this.easel.mask = this.mainMask;

        this.addChild(this.uiCont);
        this.addColorButtons();
        this.setBrush();
        this.addChild(this.brush);
        this.brush.visible = false;

        // this.addUiButtons();
        this.updateUI();
        this.addChild(this.test);

        EventApi.addEventListener(EventApi.EDIT_MODE_ON, this.editModeOn);
        EventApi.addEventListener(EventApi.EDIT_BREAK, () => this.saveCommand(false));
    }

    /**
     * Загрузить изображение и доп. иформацию о карте
     * @param img
     * @param card
     */
    public loadImage(img: HTMLImageElement, card: ICardInfo | null) {
        // console.log('%c ### LOAD IMAGE:', 'color: #f0f', card.bgSrc, card);
        this.card = card;
        this.imgSprite = PIXI.Sprite.from(img);
        this.imgSprite.width  = this.imgSize.width;
        this.imgSprite.height = this.imgSize.height;

        this.getSprite().then(sprite => {
            const imgWidth  = Math.min(img.width,  0.9 * window.innerWidth  - Painter.margins.x);
            const imgHeight = Math.min(img.height, 0.9 * window.innerHeight - Painter.margins.y);
            const imgNaturalWidth = card?.cardSize.width ?? img.naturalWidth,
                imgNaturalHeight = card?.cardSize.height ?? img.naturalHeight;
            const imgScale = Math.min(imgWidth / imgNaturalWidth, imgHeight / imgNaturalHeight);
            const width = imgNaturalWidth * imgScale, height = imgNaturalHeight * imgScale;

            // ---
            this.test.removeChildren();
            if (card && card.bgSrc) {
                this.bgSprite.width  = width;
                this.bgSprite.height = height;
                // this.faceSprite.alpha = .25;
                // this.test.addChild(this.faceSprite);
                // console.log('%c ### >>>', 'color: #f0f', imgScale1);
            }
            // ---

            this.easelWidth  =  width;
            this.easelHeight = height;
            this.imgScale = imgScale;

            this.mat.width  = this.easelWidth  + Painter.margins.x;
            this.mat.height = this.easelHeight + Painter.margins.y;
            this.updateColorButtons();
            this.brush.visible = false;

            this.mainTexture = PIXI.RenderTexture.create({ width, height });
            this.mainSprite.texture = this.mainTexture;
            this.imgSize = { width, height };

            const eraserTexture = PIXI.RenderTexture.create({ width, height });
            this.imgSprite.width  = width;
            this.imgSprite.height = height;
            this.renderer.render(this.imgSprite, { renderTexture: eraserTexture, clear: false });

            const mainMaskTexture = PIXI.RenderTexture.create({ width, height });
            this.imgSprite.filters = [new ColorOverlayFilter(0xffffff)];
            this.renderer.render(this.imgSprite, { renderTexture: mainMaskTexture, clear: false });
            this.imgSprite.filters = [];

            const scribe = new Scribe(this.renderer, false);
            const art = new PIXI.Sprite(this.imgSprite.texture);
            art.width  = width;
            art.height = height;
            scribe.src = art;
            scribe.drawScript(card ? card.actions : []);
            this.imgSprite.texture = scribe.texture;

            this.eraser!.texture = eraserTexture;
            this.mainMask!.texture = mainMaskTexture;
            this.clear();

            this.x = (window.innerWidth  - this.width)  / 2 - PixiPainter.margin.x;
            this.y = (window.innerHeight - this.height) / 2 - PixiPainter.margin.y;

            this.startDrawMode();
        }).catch(err => console.warn(err.message));
    }

    /**
     * Обновление интерфейса при изменении режима
     */
    private updateUI() {
        this.easel.cursor = (this.editMode && this.toolIdx !== Tool.Text) ? 'none' : 'default';
        this.mat.alpha = this.editMode ? ALPHA : 0;
        // this.uiCont.visible = this.editMode;
        this.uiCont.visible = false;

        this.brush.visible = false;
        this.brushOld = null;
        this.brushPos = null;
        this.brushVisible = (this.toolIdx === Tool.Brush);
    }

    /**
     * Создание абстрактной кнопки
     */
    private createButton(text: string) {
        const margins = { x: 10, y: 5 };
        const btn = new PIXI.Container;
        const btnBg = new PIXI.Sprite(PIXI.Texture.WHITE);
        const btnText = new PIXI.Text(text);
        btn.addChild(btnBg);
        btn.addChild(btnText);

        btnText.style.fontSize = 12;
        btnText.position.set(margins.x, margins.y);
        btnBg.width  = btnText.width  + 2 * margins.x;
        btnBg.height = btnText.height + 2 * margins.y;
        btnBg.tint = 0x999999;

        btn.interactive = true;
        btn.buttonMode  = true;
        return btn;
    }

    /**
     * Кнопки "Редактировать" и "Сохранить"
     * (потом вынесем во внешний интерфейс)
     */
    private addUiButtons() {
        // BTN EDIT:
        const btnEdit = this.createButton('Рисовать');
        this.addChild(btnEdit);
        btnEdit.position.set(this.easel.x + 16, this.easel.y + 16);
        btnEdit.on('pointerup', e => {
            e.data.originalEvent.stopPropagation();
            btnEdit.visible = false;
            this.editModeOn();
        });
        btnEdit.visible = false;

        // BTN SAVE:
        const btnSave = this.createButton('Сохранить');
        this.uiCont.addChild(btnSave);
        btnSave.position.set(3, 3);
        btnSave.on('pointerup', e => {
            e.data.originalEvent.stopPropagation();
            this.saveCommand();
        });
    }

    /**
     * Команда: Очистка
     */
    private clearCommand() {
        if (!this.visible) {
            return;
        }
        this.clear();
        this.clearTexture();
        MessageHelper.sendChangeCardsMessage(MessageHelper.TYPE_CLEAR_CARD, this.getData());
    }

    /**
     * Команда: Сохранение
     */
    private saveCommand(sendMessage = true) {
        if (!this.visible) {
            return;
        }
        this.visible = false;
        this.editMode = false;
        this.updateUI();

        store.dispatch('setPainterEditMode', false);
        EventApi.triggerEvent(EventApi.EDIT_MODE_OFF);
        const data = this.getData();
        if (sendMessage) {
            MessageHelper.sendChangeCardsMessage(MessageHelper.TYPE_PAINT_CARD, data);
        }
        PixiMessenger.sendEnableCardMessage([data.stackId]);
        PixiSelector.clearAll();
        this.card = null;
        this.actions = [];
    }

    /**
     * Данные для бэка
     */
    private getData() {
        return {
            cardId:  this.card?.cardId ?? 0,
            stackId: this.card?.pileId ?? 0,
            scale:   this.imgScale,
            actions: this.actions,
        };
    }

    /**
     * Дождаться загрузки текстуры
     */
    private getSprite(): Promise<PIXI.Sprite>
    {
        const bgSrc = this.card?.bgSrc ?? '';
        if (!bgSrc) {
            return new Promise(resolve => resolve(this.imgSprite));
        }
        const sprite = this.bgSprite = PIXI.Sprite.from(bgSrc);
        return new Promise((resolve, reject) => {
            if (sprite.texture.baseTexture.valid) {
                resolve(sprite)
            }
            else {
                sprite.texture.baseTexture.on('loaded', () => resolve(sprite));
                sprite.texture.baseTexture.on('error', err => reject(err));
            }
        });
    }

    /**
     * Объект - слушатель событий для рисования
     */
    private addEasel() {
        this.addChild(this.easel);
        this.easel.position.set(this.margin, this.margin);
        this.easel.interactive = true;
        this.easel.cursor = 'none';

        this.easel.on('pointerover', e => {
            e.data.originalEvent.stopPropagation();
            this.brushVisible = (this.toolIdx === Tool.Brush);
        });

        this.easel.on('pointerout', e => {
            e.data.originalEvent.stopPropagation();
            if (this.brushVisible) {
                this.pushPoints();
            }
            this.brush.visible = false;
            this.brushOld = null;
            this.brushPos = null;
            this.brushVisible = false;
        });

        this.easel.on('pointermove', e => {
            e.data.originalEvent.stopPropagation();
            if (this.brushVisible) {
                const pos = this.easel.toLocal(e.data.global);
                this.brushPos = { ...pos };
                const shiftKey = e.data.originalEvent.shiftKey;
                if (shiftKey) {
                    const snap = 16;
                    this.brushPos.x = Math.floor(this.brushPos.x / snap) * snap;
                    this.brushPos.y = Math.floor(this.brushPos.y / snap) * snap;
                }
            }
        });

        this.easel.on('pointerdown', e => {
            e.data.originalEvent.stopPropagation();
            if (!this.editMode) {
                return;
            }
            this.rightClick = this.rightClickBtn
                || e.data.originalEvent.which === 3
                || e.data.originalEvent.ctrlKey
                || e.data.originalEvent.metaKey;
            if (this.rightClick) {
                this.setBrush();
            }
            this.brushDown = true;
        });

        this.easel.on('pointerup', e => {
            e.data.originalEvent.stopPropagation();
            e.data.originalEvent.stopImmediatePropagation();
            if (this.textEdit) {
                const pos = this.easel.toLocal(e.data.global);
                this.pixiText.position.set(pos.x, pos.y);
                this.updateTextFrame();
            }
            this.onUp();
        });

        this.easel.on('pointerupoutside', e => {
            e.data.originalEvent.stopPropagation();
            this.onUp();
        });
    }

    /**
     * Завершаем полилинию
     */
    private onUp() {
        this.renderFace();
        this.brushDown = false;
        this.brushOld = null;
    }

    /**
     * Добавить контейнер для текста
     */
    private addText() {
        this.easel.addChild(this.textFrame);
        this.textFrame.visible = false;
        this.textFrame.alpha = 0.5;
        this.easel.addChild(this.textCont);
        this.pixiText.anchor.set(0.5, 0.5);
        this.textCont.addChild(this.pixiText);
        this.textCont.visible = false;
    }

    /**
     * Очистить главный спрайт
     */
    private paintMain() {
        this.renderer.render(this.imgSprite, { renderTexture: this.mainTexture, clear: false });
    }

    /**
     * Очистить главную текстуру
     */
    private clearTexture() {
        if (this.eraser) {
            this.eraser.mask = null;
            this.renderer.render(this.eraser, { renderTexture: this.mainTexture, clear: false });
            this.eraser.mask = this.eraserMask;
        }
    }

    /**
     * Очистить передний план
     */
    private paintFace() {
        this.easelFace.clear()
        // .lineStyle(1, 0x9e9e9e)
        // .drawRect(0, 0, this.easelWidth, this.easelHeight);
    }

    /**
     * Добавить кнопки для выбора цвета
     */
    private addColorButtons() {
        this.colors.forEach((color, i) => {
            this.colorButtons.push(this.addColorButton('btn_' + i, color, 0, 0));
        });
        this.updateColorButtons();
    }

    /**
     * Разместить кнопки для выбора цвета
     */
    private updateColorButtons() {
        if (this.colors.length < 2) {
            console.warn('colors.length =', this.colors.length);
            return;
        }
        const x0 = this.margin + this.brushSize / 2,
            dx = (this.easelWidth - this.brushSize) / (this.colors.length - 1),
            y0 = this.easelHeight + 2 * this.margin;
        this.colorButtons.forEach((btn, i) => btn.position.set(x0 + i * dx, y0));
    }

    /**
     * Кнопка для выбора цвета
     * @param name
     * @param color
     * @param x
     * @param y
     */
    private addColorButton(name: string, color: number, x: number, y: number): PIXI.Graphics {
        const btn = new PIXI.Graphics();
        btn.lineStyle(2, 0xffffff).beginFill(color).drawCircle(0, 0, this.brushSize / 2).endFill();
        this.uiCont.addChild(btn);
        btn.name = name;
        btn.position.set(x, y);

        btn.interactive = true;
        btn.buttonMode  = true;
        btn.on('pointerup', e => {
            e.data.originalEvent.stopPropagation();
            const idx = +btn.name.split('_')[1];
            const rightClick = e.data.originalEvent.which === 3 || e.data.originalEvent.ctrlKey;
            if (rightClick) {
                // this.setBgColor(idx);
            }
            else {
                this.colorIdx = idx;
                this.setBrush();
            }
        });
        return btn;
    }

    /**
     * Текущий цвет
     */
    private get color() {
        // return (this.colorIdx in this.colors) ? this.colors[this.colorIdx] : 0x9e9e9e;
        return this.colorIdx ? this.playerColor : 0x010101;
    }

    /**
     * Установить кисть
     */
    private setBrush() {
        this.setBrushColor(this.color);
    }

    /**
     * Установить цвет кисти
     */
    private setBrushColor(color: number) {
        const size = this.rightClickBtn ? this.eraserSize : this.brushSize;
        this.textStyle.fill = color;
        this.pixiText.style = new PIXI.TextStyle(this.textStyle);
        this.updateTextFrame();
        this.brush.clear()
            .lineStyle(1, this.rightClick ? 0x333333 : 0xffffff)
            .beginFill(this.rightClick ? 0xffffff : color)
            .drawCircle(0, 0, size / 2)
            .endFill();
    }

    /**
     * Установить фоновый цвет
     * @param idx
     */
    private setBgColor(idx: number) {
        if (this.bgColorIdx === idx) {
            this.bgColorIdx = -1;
        }
        else {
            this.bgColorIdx = idx;
        }
        this.bgColor = (idx in this.bgColors) ? this.bgColors[idx] : 0xffffff;
        const filters = this.bgColorIdx < 0 ? [] :
            [new ColorReplaceFilter(0xffffff, this.bgColor)];
        this.easelFace.filters  = filters;
        this.mainSprite.filters = filters;
    }

    /**
     * Обновить таймер
     */
    public updateTime(delta: number) {
        this.elapsedTime += delta;
        const DT = 32;
        if (this.elapsedTime > DT) {
            this.elapsedTime -= DT;
            if (this.textEdit) {
                this.textFrameWhite = !this.textFrameWhite;
                this.updateTextFrame();
            }
        }
    }

    /**
     * Обновить рамку текста
     */
    public updateTextFrame() {
        this.textFrame.clear();
        const width = this.pixiText.width + 16, height = this.pixiText.height + 16;
        this.textFrame.lineStyle(2, this.textFrameWhite ? 0xffffff : this.color)
            .drawRect(.5+this.pixiText.x - width/2, .5+this.pixiText.y - height/2, width, height);
    }

    /**
     * Кисть в новой позиции?
     */
    private isNewPos(a: { x: number, y: number }, b: { x: number, y: number }) {
        const minStep = 2;
        return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y)) >= minStep;
    }

    /**
     * Применить изменения
     */
    public update(delta: number) {
        this.updateTime(delta);
        if (this.brushVisible && this.brushPos) {
            this.brush.position.set(this.brushPos.x + this.easel.x, this.brushPos.y + this.easel.y);
            this.brush.visible = this.editMode;
            if (this.brushDown) {
                let pushPos = true;
                if (this.brushOld) {
                    pushPos = this.isNewPos(this.brushOld, this.brushPos);
                    const target = this.rightClick ? this.eraserMask : this.easelFace;
                    target
                        .lineStyle({
                            color: this.rightClick ? 0xffffff : this.color,
                            cap:  PIXI.LINE_CAP.ROUND,
                            join: PIXI.LINE_JOIN.ROUND,
                            width: this.rightClick ? this.eraserSize : this.brushSize,
                        })
                        .moveTo(this.brushOld.x, this.brushOld.y)
                        .lineTo(this.brushPos.x, this.brushPos.y + (pushPos? 0: 1));
                }
                else {
                    this.points = [];
                }

                if (pushPos) {
                    this.points.push(this.brushPos);
                }
                this.brushOld = { ...this.brushPos };
            }
        }
    }

    /**
     * Очистить текстуры
     */
    private clear() {
        this.actions = [];
        this.points = [];
        this.paintFace();
        this.paintMain();
    }

    /**
     * Признак: режим добавления текста
     */
    private get textTool() {
        return this.toolIdx === Tool.Text;
    }

    /**
     * Рендер текста в текстуру
     */
    private renderText() {
        if (this.text.length) {
            this.actions.push({
                name: 'Text',
                color: this.textStyle.fill,
                size: this.textStyle.fontSize,
                text: this.pixiText.text,
                angle: this.pixiText.angle,
                x: this.pixiText.x,
                y: this.pixiText.y,
            });
            this.renderer.render(this.textCont, { renderTexture: this.mainTexture, clear: false });
        }
        this.textEdit = false;
        this.textFrame.visible = false;
    }

    /**
     * Добавить новое действие
     */
    private pushPoints() {
        if (1 === this.points.length) {
            const p0 = this.points[0];
            this.points.push({ x: p0.x, y: p0.y + 1});
        }
        if (this.points.length) {
            this.actions.push({
                name: this.rightClick? 'Eraser': 'Brush',
                color: this.color,
                size: this.rightClick? this.eraserSize: this.brushSize,
                points: this.points,
            });
            this.points = [];
        }
    }

    /**
     * Рендер полилинии в текстуру
     */
    private renderFace() {
        this.pushPoints();
        if (this.rightClick) {
            if (this.eraser) {
                this.renderer.render(this.eraser, { renderTexture: this.mainTexture, clear: false });
            }
            this.eraserMask.clear();
        }
        else {
            // const filters = this.easelFace.filters;
            // this.easelFace.filters = [];
            this.renderer.render(this.easelFace, { renderTexture: this.mainTexture, clear: false });
            // this.easelFace.filters = filters;
            this.paintFace();
        }
    }

    /**
     * Изменить размер кисти
     */
    private changeBrushSize(delta: number) {
        const brushSizeMin = 8, brushSizeMax = 128;
        if (this.rightClickBtn) {
            this.eraserSize += delta;
            this.eraserSize = Math.min(Math.max(brushSizeMin, this.eraserSize), brushSizeMax);
        }
        else {
            this.brushSize += delta;
            this.brushSize = Math.min(Math.max(brushSizeMin, this.brushSize), brushSizeMax);
        }
        this.setBrush();
    }

    /**
     * Изменить размер текста
     */
    private changeFontSize(fontSize: number) {
        this.textStyle.fontSize = fontSize;
        this.pixiText.style = new PIXI.TextStyle(this.textStyle);
    }
    private changeFontSizeDelta(delta: number) {
        const fontSizeMin = 12, fontSizeMax = 96;
        let fontSize = +(this.textStyle.fontSize || 0);
        fontSize += delta;
        fontSize = Math.min(Math.max(fontSizeMin, fontSize), fontSizeMax);
        this.changeFontSize(fontSize);
    }

    /**
     * Установить инструмент
     */
    private setTool(tool: number) {
        const b = (tool === Tool.Brush);
        this.toolIdx = tool;
        this.easel.cursor = b ? 'none' : 'default';
        this.brushVisible = b;
        this.textCont.visible = this.textTool;
    }

    /**
     * Установить текст
     */
    private setText(text: string) {
        this.text = text;
        this.pixiText.text = this.text;
        this.updateTextFrame();
    }

    /**
     * Горячие клавиши
     */
    public onKeyup(e: KeyboardEvent): void
    {
        this.textEdit ? this.onKeyupText(e) : this.onKeyupDraw(e);
    }

    /**
     * Горячие клавиши: набор текста
     */
    private onKeyupText(e: KeyboardEvent): void
    {
        const key = e.key, code = e.code;
        if (1 === key.length && !code.includes('Numpad')) {
            this.setText(this.text + key);
        }
        switch (code) {
            case 'Backspace':
                if (this.text.length) {
                    this.setText(this.text.slice(0, this.text.length - 1));
                }
                break;
            case 'Enter':
            case 'NumpadEnter':
                if (e.shiftKey) {
                    this.setText(this.text + '\r\n');
                }
                else {
                    this.renderText();
                }
                break;
            case 'Escape':
                this.setText('');
                this.renderText();
                break;
            case 'Space':
                this.text += ' ';
                this.pixiText.text = this.text;
                break;
            // default:
            //     console.log('%c ### Text Tool:', 'color: #f0f', e.code, e);
        }
    }

    /**
     * Горячие клавиши: кисть / ластик
     */
    private onKeyupDraw(e: KeyboardEvent): void
    {
        switch (e.code) {
            case 'Delete':
                this.clearCommand();
                break;
            case 'Escape':
                if (store.getters.getPainterEditMode) {
                    this.onUp();
                    this.saveCommand(false);
                }
                break;
            case 'Space':
                if (store.getters.getPainterEditMode) {
                    this.onUp();
                    this.saveCommand();
                }
                break;
            case 'AltLeft':
            case 'ControlLeft':
            case 'ShiftLeft':
                break;
            // default:
            //     console.log('%c ### Key Up:', 'color: #f0f', e.code, e);
        }
    }

    /**
     * Войти в режим рисования
     */
    public startDrawMode() {
        const settings = store.getters.getGameState?.tasks?.state?.activity?.settings;
        const canNotDraw = !!(settings && false === settings[CAN_DRAW]);
        if (canNotDraw) {
            return;
        }
        store.dispatch('setPainterEditMode', true);
        EventApi.triggerEvent(EventApi.EDIT_MODE_KEY);
    }

    /**
     * Поля вокруг текстуры
     */
    public static get margins() {
        return { x: 2 * MARGIN, y: 3 * MARGIN }
    }

    /**
     * Признак: редактирование активировано
     */
    public get inEditMode() {
        return this.editMode;
    }

    /**
     * Активировать редактирование
     */
    public editModeOn() {
        // EventApi.triggerEvent(EventApi.EDIT_MODE_ON);
        this.editMode = (Tool.Select !== this.toolIdx);
        this.updateUI();
    }

    private startText() {
        this.pixiText.position.set(this.easelWidth/2, this.easelHeight/2);
        this.setText('');
        this.setTool(Tool.Text);
        this.brush.visible = false;
        this.brushPos = null;
        this.textEdit = true;
        this.textFrame.visible = true;
    }

    /**
     * Клик по кнопке выбора инструмента
     * @param btnId
     */
    public btnHandler(btnId: number) {
        this.rightClickBtn = false;
        this.rightClick = false;
        if (this.textTool) {
            this.renderText();
        }
        switch (btnId) {
            case Tool.Select:
                this.setTool(Tool.Select);
                this.editMode = false;
                break;
            case Tool.Brush:
                break;
            case Tool.Text:
                this.startText();
                break;
            case Tool.Eraser:
                this.rightClickBtn = true;
                this.rightClick = true;
                break;
            case Tool.Trash:
                this.clearCommand();
                break;
            default:
                console.warn('BTN ID:', btnId);
        }
        if (Tool.Select !== btnId && Tool.Text !== btnId) {
            this.editMode = true;
            this.setTool(Tool.Brush);
            this.setBrush();
        }
    }

    /**
     * Клик по кнопке выбора цвета
     * @param btnId
     * @param btnColor
     */
    public colorBtnHandler(btnId: number, btnColor: string) {
        if (btnId) {
            this.playerColor = parseInt(btnColor.slice(1), 16);
        }
        this.colorIdx = btnId;
        this.setBrush();
    }

    /**
     * Клик по кнопке выбора размера инструмента
     * @param btnId
     * @param tool
     */
    public sizeBtnHandler(btnId: number, tool: number) {
        if (tool === Tool.Eraser) {
            this.eraserSize = [16, 64, 256][btnId];
        }
        else if (tool === Tool.Brush) {
            this.brushSize = [8, 32, 128][btnId];
        }
        this.setBrush();
    }

    /**
     * Клик по кнопке выбора размера текста
     * @param btnId
     * @param tool
     */
    public fontBtnHandler(btnId: number, tool: number) {
        if (tool === Tool.Text) {
            this.changeFontSize([12, 54, 96][btnId]);
            this.updateTextFrame();
        }
    }

}
